import { create } from 'zustand';
import { AuthState, BanDetails, PlayerRole } from '../types';
import { authApi } from '../services/api';

export interface LoginResult {
  success: boolean;
  message?: string;
  banned?: boolean;
  // CrazyGames guest only: the device id was already claimed by a linked account, so
  // the caller should mint a fresh device id and retry.
  guestClaimed?: boolean;
}

const BAN_KEY = 'ban_info';
const SOURCE_KEY = 'auth_source';

// How the current session was authenticated. Persisted so it survives a reload
// (checkAuth validates the cookie but doesn't re-run the login flow). 'dev' = the
// username login (itch/direct); 'crazygames' = CrazyGames account or guest.
export type AuthSource = 'dev' | 'crazygames';

function loadSource(): AuthSource | null {
  const s = localStorage.getItem(SOURCE_KEY);
  return s === 'dev' || s === 'crazygames' ? s : null;
}

// Persist ban details so the /banned page survives a page reload.
function loadBan(): BanDetails | null {
  try {
    const raw = localStorage.getItem(BAN_KEY);
    return raw ? (JSON.parse(raw) as BanDetails) : null;
  } catch {
    return null;
  }
}

function saveBan(ban: BanDetails | null) {
  if (ban) localStorage.setItem(BAN_KEY, JSON.stringify(ban));
  else localStorage.removeItem(BAN_KEY);
}

// Extracts ban details from a 403 response, or null if it isn't a ban.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function banFromError(error: any): BanDetails | null {
  const resp = error?.response;
  if (resp?.status === 403 && resp.data?.banned) {
    return resp.data as BanDetails;
  }
  return null;
}

interface AuthStore extends AuthState {
  banInfo: BanDetails | null;
  // false until the initial checkAuth() resolves — route guards must wait for it
  // so a reload doesn't redirect before the session cookie has been validated.
  authChecked: boolean;
  authSource: AuthSource | null;
  devLogin: (username: string, role: string, password?: string) => Promise<LoginResult>;
  crazyGamesLogin: (token: string, guestDeviceId?: string) => Promise<LoginResult>;
  crazyGamesGuest: (deviceId: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setAuth: (auth: Partial<AuthState>) => void;
  clearBan: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: false,
  userId: null,
  username: null,
  token: null,
  role: null,
  banInfo: loadBan(),
  authChecked: false,
  authSource: loadSource(),

  devLogin: async (username: string, role: string, password?: string): Promise<LoginResult> => {
    try {
      const response = await authApi.devLogin(username, role, password);
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem(SOURCE_KEY, 'dev');
        saveBan(null);
        set({
          isAuthenticated: true,
          userId: response.data.userId,
          username: response.data.username,
          token: response.data.token,
          role: (response.data.role as PlayerRole) || PlayerRole.User,
          banInfo: null,
          authChecked: true,
          authSource: 'dev',
        });
        return { success: true };
      }
      return { success: false, message: 'Dev login failed' };
    } catch (error: unknown) {
      const ban = banFromError(error);
      if (ban) {
        saveBan(ban);
        set({ banInfo: ban });
        return { success: false, banned: true, message: ban.message };
      }
      const axiosErr = error as { response?: { status?: number; data?: { message?: string } } };
      const msg = axiosErr.response?.data?.message ?? 'Dev login failed';
      return { success: false, message: msg };
    }
  },

  crazyGamesLogin: async (token: string, guestDeviceId?: string): Promise<LoginResult> => {
    try {
      const response = await authApi.crazyGamesLogin(token, guestDeviceId);
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem(SOURCE_KEY, 'crazygames');
        saveBan(null);
        set({
          isAuthenticated: true,
          userId: response.data.userId,
          username: response.data.username,
          token: response.data.token,
          role: (response.data.role as PlayerRole) || PlayerRole.User,
          banInfo: null,
          authChecked: true,
          authSource: 'crazygames',
        });
        return { success: true };
      }
      return { success: false, message: 'CrazyGames login failed' };
    } catch (error: unknown) {
      const ban = banFromError(error);
      if (ban) {
        saveBan(ban);
        set({ banInfo: ban });
        return { success: false, banned: true, message: ban.message };
      }
      return { success: false, message: 'CrazyGames login failed' };
    }
  },

  crazyGamesGuest: async (deviceId: string): Promise<LoginResult> => {
    try {
      const response = await authApi.crazyGamesGuest(deviceId);
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem(SOURCE_KEY, 'crazygames');
        saveBan(null);
        set({
          isAuthenticated: true,
          userId: response.data.userId,
          username: response.data.username,
          token: response.data.token,
          role: (response.data.role as PlayerRole) || PlayerRole.User,
          banInfo: null,
          authChecked: true,
          authSource: 'crazygames',
        });
        return { success: true };
      }
      return { success: false, message: 'Guest session failed' };
    } catch (error: unknown) {
      // 409 → the device id was claimed by a linked account; caller mints a fresh id.
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 409) return { success: false, guestClaimed: true };
      const ban = banFromError(error);
      if (ban) {
        saveBan(ban);
        set({ banInfo: ban });
        return { success: false, banned: true, message: ban.message };
      }
      return { success: false, message: 'Guest session failed' };
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore logout errors
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem(SOURCE_KEY);
      saveBan(null);
      set({ isAuthenticated: false, userId: null, username: null, token: null, role: null, banInfo: null, authChecked: true, authSource: null });
    }
  },

  checkAuth: async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        localStorage.removeItem(SOURCE_KEY);
        set({ isAuthenticated: false, userId: null, username: null, token: null, role: null, authChecked: true, authSource: null });
        return;
      }

      const response = await authApi.getCurrentUser();
      if (response.data.isAuthenticated) {
        set({
          isAuthenticated: true,
          userId: response.data.userId,
          username: response.data.username || null,
          token,
          role: (response.data.role as PlayerRole) || PlayerRole.User,
          authChecked: true,
        });
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem(SOURCE_KEY);
        set({ isAuthenticated: false, userId: null, username: null, token: null, role: null, authChecked: true, authSource: null });
      }
    } catch (error: unknown) {
      // A player banned mid-session is kicked out here and routed to /banned.
      const ban = banFromError(error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem(SOURCE_KEY);
      if (ban) {
        saveBan(ban);
        set({ isAuthenticated: false, userId: null, username: null, token: null, role: null, banInfo: ban, authChecked: true, authSource: null });
      } else {
        set({ isAuthenticated: false, userId: null, username: null, token: null, role: null, authChecked: true, authSource: null });
      }
    }
  },

  setAuth: (auth: Partial<AuthState>) => {
    set((state) => ({ ...state, ...auth }));
  },

  clearBan: () => {
    saveBan(null);
    set({ banInfo: null });
  },
}));
